import { useState } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import FilterDropdown, {
  ActiveFilters,
  ButtonFilter,
} from '../../../components/ModelInstanceFilter';
import type { IProps as IFilterDropdownProps } from '../../../components/ModelInstanceFilter';
import { useToggleVisibility } from '../../../hooks';
import { CloseOutlined } from '@ant-design/icons';
import useFilterOptions from './useFilterOptions';
import type { IModelInstancesOptions } from './useFilterOptions';

export interface ISelectOption {
  label: string;
  value: string;
}

export interface IFilterProps extends Partial<IFilterDropdownProps<any>> {
  key: string;
  type?: 'filter';
  defaultVisible: boolean;
  filter: string;
  name: string;
  options?: ISelectOption[];
  modelInstanceOptions?: IModelInstanceOptions;
  modelInstancesOptions?: IModelInstancesOptions;
  order?: number;
}

/**
 * Options for the model instance uniq path filter
 * API: /model-instances/filter-values
 */
export interface IModelInstanceOptions {
  modelIds: string[];
  filter?: object; // filter to apply to the model instances
  filterPath: string; // tests[].performer
  searchPath: string; // tests[].performer.name
  transformer: string; // jmespath expression
}

export type { IModelInstancesOptions };

interface IProps extends Partial<IFilterDropdownProps<any>> {
  name: string;
  filter: string; // product.inn, tests[].performer.id
  options?: ISelectOption[];
  modelInstanceOptions?: IModelInstanceOptions;
  modelInstancesOptions?: IModelInstancesOptions;
  default?: string[];
  context?: Record<string, any>;
  onApply: (selected: string[]) => void;
  onRemove?: () => void;
}

const Filter: React.FC<IProps> = ({
  filter,
  options,
  modelInstanceOptions,
  modelInstancesOptions,
  name,
  onApply,
  default: initialSelected,
  onRemove,
  context,
  ...rest
}) => {
  const [selected, setSelected] = useState<string[]>(initialSelected || []);
  const { isVisible, show, hide } = useToggleVisibility();

  const {
    filters,
    isLoading,
    onPopupScroll,
    isLocalSearch,
    search,
    setSearch,
  } = useFilterOptions({
    options,
    modelInstanceOptions,
    modelInstancesOptions,
    isVisible,
    context,
  });

  const handleApply = (newSelection: string[]) => {
    setSelected(newSelection);
    onApply(newSelection);
    hide();
  };

  return (
    <FilterDropdown
      {...rest}
      onScroll={onPopupScroll}
      name={name}
      filters={filters}
      selected={selected}
      onApply={handleApply}
      search={isLocalSearch ? undefined : search}
      onSearch={isLocalSearch ? undefined : setSearch}
      isLoading={isLoading}
      visible={isVisible}
      onShow={show}
      onHide={hide}
      renderButton={({ numberOfActive }: { numberOfActive: number }) => (
        <ButtonFilter>
          <span>{name}</span>
          {numberOfActive > 0 && (
            <ActiveFilters>
              <span>{numberOfActive}</span>
            </ActiveFilters>
          )}
          {onRemove && (
            <RemoveButton
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={onRemove}
            />
          )}
        </ButtonFilter>
      )}
    />
  );
};

const RemoveButton = styled(Button)`
  padding: 4px;
  height: auto;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

export default Filter;
